// src/composables/useApi.js
import { baseURL } from '../config/api';
import { auth } from '../config/firebase'; // Import your Firebase auth instance
import { useAuthStore } from '../stores/authStore';

// This is the central composable for all API interactions.
// It automatically handles authentication and base URL configuration.

export const useApi = () => {
  const authStore = useAuthStore();

  const _getAuthHeader = async () => {
    // Wait for Firebase to initialize if it hasn't yet
    let user = auth.currentUser;
    
    if (!user) {
      // Create a promise that waits for the auth state to change
      user = await new Promise((resolve) => {
        const unsubscribe = auth.onAuthStateChanged((u) => {
          unsubscribe();
          resolve(u);
        });
        // Safety timeout to prevent infinite hang if no internet
        setTimeout(() => resolve(null), 5000);
      });
    }

    if (!user) {
      console.warn('[useApi] User is not authenticated after waiting. Making request without Authorization header.');
      return {};
    }
    try {
      const token = await user.getIdToken(); 
      return { 'Authorization': `Bearer ${token}` };
    } catch (error) {
      console.error('[useApi] Error getting auth token:', error);
      // If getting the token fails, it might be a good idea to sign the user out
      // as their session might be corrupted.
      authStore.logout();
      throw new Error('Failed to get authentication token.');
    }
  };

  const _fetch = async (path, options = {}, retries = 0, backoff = 1000) => {
    const headers = await _getAuthHeader();
    
    const config = {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        ...headers,
        ...options.headers,
      },
    };

    const url = `${baseURL}${path}`;
    
    console.log(`[useApi] ==> Sending ${config.method || 'GET'} request to: ${url}`);

    try {
      const response = await fetch(url, config);

      console.log(`[useApi] <== Received response with status: ${response.status}`);

      if (response.status === 401) {
        console.warn(`[useApi] Authentication error (401) for ${path}. Attempting token refresh...`);
        
        // Try to force refresh the token once
        const user = auth.currentUser;
        if (user && retries === 0) { // Only retry once for 401
            try {
                console.log('[useApi] Forcing token refresh...');
                const newToken = await user.getIdToken(true);
                // Update config with new token and retry
                config.headers['Authorization'] = `Bearer ${newToken}`;
                return _fetch(path, options, 1, 500); // Retry exactly once
            } catch (refreshError) {
                console.error('[useApi] Token refresh failed:', refreshError);
            }
        }

        // If we reach here, it means either no user or refresh failed or retry failed
        if (auth.currentUser) {
            console.error(`[useApi] Permanent 401 error for authenticated user. Forcing logout.`);
            authStore.logout();
            throw new Error('Unauthorized. Please login again.');
        } else {
            console.warn(`[useApi] 401 error for guest user on path: ${path}. Checking if it's a public route.`);
            // Allow public routes to fail silently or be handled by the component
            if (path.startsWith('/public/')) {
                const errorData = await response.json().catch(() => ({}));
                const message = errorData.message || 'Access Denied';
                const managedError = new Error(message);
                managedError.code = errorData.error || 'UNAUTHORIZED';
                managedError.isManaged = true;
                managedError.status = 401;
                managedError.data = errorData;
                throw managedError;
            }
            throw new Error('Guest Unauthorized');
        }
      }

      if (response.status === 403) {
        // 403 is Forbidden - often used for 'Out of credits', 'Trial expired', or Quotas
        // We should NOT logout the user, just throw the error to be handled by the UI
        const errorData = await response.json().catch(() => ({}));
        const message = errorData.message || 'Access Forbidden. Check your balance or trial status.';
        
        // Throw a specialized error object that UI can easily check
        const managedError = new Error(message);
        managedError.code = errorData.error || 'FORBIDDEN';
        managedError.isManaged = true;
        throw managedError;
      }

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`[useApi] API Error Response Text:`, errorText);
        throw new Error(`API error with status ${response.status}`);
      }
      
      const data = await response.json();
      return data;

    } catch (error) {
      // Retry logic for network errors (only for GET)
      if (retries > 0 && (!options.method || options.method === 'GET')) {
          console.warn(`[useApi] Request failed. Retrying... (${retries} left). Error: ${error.message}`);
          await new Promise(resolve => setTimeout(resolve, backoff));
          return _fetch(path, options, retries - 1, backoff * 2);
      }

      // Don't log "Managed" errors as CRITICAL
      if (!error.isManaged) {
        console.error(`[useApi] CRITICAL: Caught an error in _fetch for path (${path}):`, error);
      }
      throw error;
    }
  };

  return {
    // Enable retries for GET requests by default (3 retries)
    get: (path, options = {}) => _fetch(path, { method: 'GET', ...options }, 3),
    post: (path, body, options = {}) => _fetch(path, { method: 'POST', body: JSON.stringify(body), ...options }),
    put: (path, body, options = {}) => _fetch(path, { method: 'PUT', body: JSON.stringify(body), ...options }),
    delete: (path, options = {}) => _fetch(path, { method: 'DELETE', ...options }),
    download: async (path, body, options = {}) => {
      const headers = await _getAuthHeader();
      const config = {
        method: 'POST',
        body: JSON.stringify(body),
        ...options,
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/pdf',
          ...headers,
          ...options.headers,
        },
      };
      const url = `${baseURL}${path}`;
      console.log(`[useApi] ==> Sending POST request to: ${url}`);
      try {
        const response = await fetch(url, config);
        console.log(`[useApi] <== Received response with status: ${response.status}`);
        if (!response.ok) {
          const errorText = await response.text();
          console.error(`[useApi] API Error Response Text:`, errorText);
          throw new Error(`API error with status ${response.status}`);
        }
        return await response.blob();
      } catch (error) {
        console.error(`[useApi] CRITICAL: Caught an error in download for path (${path}):`, error);
        throw error;
      }
    },
  };
};
